// Shared building blocks for the Web Categories table rows: style tokens,
// thumbnail colour helper, visibility pill and POS-mapping badge. Split out of
// index.tsx when the table became a recursive 3-level tree.

import { pantryColors, pantryTypography } from '@pantry/ui'
import type React from 'react'
import type { LinkedPosCategoryRef } from '../../types'

export const p = pantryColors
export const F = pantryTypography.fontFamily

export const SHOW_SELECT_CHECKBOX = true

export const checkboxStyle: React.CSSProperties = {
  width: 15,
  height: 15,
  accentColor: p.brand.primary,
  cursor: 'pointer',
}

export const thBase: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 11.5,
  fontWeight: 600,
  color: p.gray[500],
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  borderBottom: `1px solid ${p.gray[200]}`,
  whiteSpace: 'nowrap',
  background: p.white,
}

export const tdBase: React.CSSProperties = {
  padding: '0 12px',
  borderBottom: `1px solid ${p.gray[100]}`,
  verticalAlign: 'middle',
  height: 52,
}

const thumbColors = [
  p.brand.light,
  p.teal[50],
  p.amber[100],
  p.red[50],
  p.purple[50],
]
const thumbTextColors = [
  p.brand.hover,
  p.teal[700],
  p.amber[800],
  p.red[800],
  p.purple[700],
]
export function thumbColor(name: string) {
  const i = name.charCodeAt(0) % thumbColors.length
  return { bg: thumbColors[i], fg: thumbTextColors[i] }
}

export const VisibilityPill: React.FC<{
  visible: boolean
  onToggle: () => void
}> = ({ visible, onToggle }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation()
      onToggle()
    }}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '3px 9px',
      borderRadius: 20,
      fontSize: 11.5,
      fontWeight: 600,
      cursor: 'pointer',
      border: 'none',
      fontFamily: F,
      background: visible ? p.brand.light : p.gray[100],
      color: visible ? p.brand.primary : p.gray[500],
      transition: 'all 0.15s',
    }}
    title={visible ? 'Click to hide' : 'Click to show'}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: visible ? p.brand.primary : p.gray[400],
        flexShrink: 0,
        display: 'inline-block',
      }}
    />
    {visible ? 'Visible' : 'Hidden'}
  </button>
)

export const PosBadge: React.FC<{
  linked: LinkedPosCategoryRef[]
}> = ({ linked }) => {
  const isMapped = linked.length > 0
  const linkedName = isMapped
    ? linked.map((r) => r.label).join(', ')
    : undefined
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 8px',
          borderRadius: 20,
          fontSize: 11.5,
          fontWeight: 600,
          background: isMapped ? p.brand.light : '#FEF3C7',
          color: isMapped ? p.brand.primary : '#B45309',
          width: 'fit-content',
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: isMapped ? p.brand.primary : '#B45309',
            flexShrink: 0,
            display: 'inline-block',
          }}
        />
        {isMapped ? `${linked.length} linked` : 'Not mapped'}
      </div>
      {linkedName && (
        <div
          style={{
            fontSize: 11,
            color: p.gray[500],
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 150,
            fontFamily: F,
          }}
          title={linkedName}
        >
          {linkedName}
        </div>
      )}
    </div>
  )
}
